import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Activity, CheckCircle2, XCircle, Loader2, RefreshCcw, Plug } from "lucide-react";

/**
 * Instrumentation Check — confirms every GA4 event is firing in
 * production, and that the six "Mark as conversion" events have
 * actually been flipped to conversion in the GA4 Admin UI.
 *
 * Reads /api/admin/founder/instrumentation-check. Cleanly falls back to
 * "not configured" if GA4 Data API credentials are missing.
 */
export default function InstrumentationCheck() {
    const [state, setState] = useState({ loading: true, error: null, data: null });

    const load = async () => {
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            const r = await api.get("/admin/founder/instrumentation-check");
            setState({ loading: false, error: null, data: r.data });
        } catch (e) {
            setState({
                loading: false,
                error: e?.response?.data?.detail || "Couldn't run check.",
                data: null,
            });
        }
    };

    useEffect(() => { load(); }, []);

    if (state.loading) {
        return (
            <section className="ml-card p-6 flex items-center gap-2 text-[#6E7A74]" data-testid="instr-loading">
                <Loader2 className="animate-spin" size={18} /> Verifying GA4 events…
            </section>
        );
    }
    if (state.error) {
        return (
            <section className="ml-card p-6" data-testid="instr-error">
                <p className="text-[#1F2624]">Couldn&apos;t verify GA4 instrumentation.</p>
                <p className="text-sm text-[#4C5551] mt-1">{state.error}</p>
                <button onClick={load} className="ml-btn-secondary mt-4">
                    <RefreshCcw size={16} strokeWidth={1.6} /> Try again
                </button>
            </section>
        );
    }

    const d = state.data || {};
    const notConfigured = d.status === "not_configured";
    const errored = d.status === "error";
    const events = d.events || [];
    const conversions = d.conversions || (events.filter((e) => e.is_conversion_target));
    const tracked = events.length ? events : (d.tracked_events || []).map((n) => ({
        name: n,
        is_conversion_target: (d.conversion_events || []).includes(n),
        fired: false,
        marked_as_conversion_in_ga4: false,
    }));

    return (
        <section className="ml-card p-5 sm:p-6" data-testid="instrumentation-check">
            <header className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <span className="rounded-full bg-teal-soft text-teal-dark p-1.5">
                        <Activity size={14} strokeWidth={2} />
                    </span>
                    <div>
                        <h3 className="font-heading text-base font-semibold text-[#1F2624] leading-tight">
                            GA4 instrumentation check
                        </h3>
                        <p className="text-xs text-[#6E7A74] leading-tight">
                            Last 28 days · verifies every tracked event has fired + 6 conversions are marked.
                        </p>
                    </div>
                </div>
                <button
                    onClick={load}
                    className="text-xs inline-flex items-center gap-1 text-[#4C5551] hover:text-teal-dark"
                    data-testid="instr-refresh"
                >
                    <RefreshCcw size={12} strokeWidth={1.8} /> Refresh
                </button>
            </header>

            {notConfigured && (
                <div className="mt-4 flex items-start gap-3 bg-cream-light rounded-xl px-4 py-3 text-sm text-[#4C5551]" data-testid="instr-not-configured">
                    <Plug size={16} strokeWidth={1.8} className="mt-0.5 text-teal-dark shrink-0" />
                    <div>
                        <p className="text-[#1F2624] font-medium">Connect GA4 Data API to verify events live</p>
                        <p className="mt-1">{d.config_hint}</p>
                    </div>
                </div>
            )}

            {errored && (
                <div className="mt-4 text-sm text-coral-dark bg-coral-light rounded-xl px-4 py-3" data-testid="instr-errored">
                    {d.error}
                </div>
            )}

            {/* Headline pills */}
            {!notConfigured && !errored && (
                <div className="mt-4 grid grid-cols-2 gap-3">
                    <HeadlinePill
                        label="All caregiver events firing"
                        ok={d.all_events_firing}
                        testid="instr-all-firing"
                    />
                    <HeadlinePill
                        label="All 6 conversion events marked"
                        ok={d.all_conversions_marked}
                        testid="instr-all-conversions"
                    />
                </div>
            )}

            {/* Conversion events table */}
            <h4 className="mt-5 mb-2 text-[10px] uppercase tracking-wider text-[#6E7A74] font-semibold">
                Conversion events (6 expected)
            </h4>
            <ul className="space-y-1.5" data-testid="instr-conversions-list">
                {conversions.map((e, i) => (
                    <EventRow key={i} evt={e} isConversion testid={`instr-conv-${e.name}`} />
                ))}
            </ul>

            {/* All tracked events */}
            <h4 className="mt-5 mb-2 text-[10px] uppercase tracking-wider text-[#6E7A74] font-semibold">
                Every tracked caregiver action ({tracked.length})
            </h4>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5" data-testid="instr-events-list">
                {tracked.map((e, i) => (
                    <EventRow key={i} evt={e} testid={`instr-evt-${e.name}`} />
                ))}
            </ul>
        </section>
    );
}

function HeadlinePill({ label, ok, testid }) {
    return (
        <div
            className={`rounded-xl px-3 py-2.5 flex items-center gap-2 ${ok ? "bg-stage_early/15 text-stage_early-dark" : "bg-coral-light text-coral-dark"}`}
            data-testid={testid}
        >
            {ok ? <CheckCircle2 size={18} strokeWidth={2} /> : <XCircle size={18} strokeWidth={2} />}
            <span className="text-sm font-semibold">{label}</span>
        </div>
    );
}

function EventRow({ evt, isConversion, testid }) {
    const passed = isConversion ? evt.marked_as_conversion_in_ga4 : evt.fired;
    return (
        <li
            className="flex items-center justify-between gap-3 text-sm rounded-lg border border-cream-dark bg-white px-3 py-1.5"
            data-testid={testid}
        >
            <div className="flex items-center gap-2 min-w-0">
                {passed ? (
                    <CheckCircle2 size={14} strokeWidth={2} className="text-stage_early-dark shrink-0" />
                ) : (
                    <XCircle size={14} strokeWidth={2} className="text-coral-dark shrink-0" />
                )}
                <code className="truncate text-[12px] text-[#1F2624]" title={evt.name}>{evt.name}</code>
                {evt.is_conversion_target && !isConversion && (
                    <span className="text-[9px] uppercase tracking-wider text-teal-dark font-semibold shrink-0">conv</span>
                )}
            </div>
            <div className="text-[11px] text-[#6E7A74] tabular-nums shrink-0">
                {evt.event_count || 0} fired
                {isConversion && (
                    <span className={evt.marked_as_conversion_in_ga4 ? "ml-2 text-stage_early-dark" : "ml-2 text-coral-dark"}>
                        {evt.marked_as_conversion_in_ga4 ? "· marked" : "· NOT marked"}
                    </span>
                )}
            </div>
        </li>
    );
}
